import { callDelete, get, post, put } from "@/services/api.services";
import { Attribute, RelationOptions } from "@/services/things/models";
import { SavedObject } from "@/services/commonModels";

export type RegistryEntry = {
    id: string;
    displayName: string;
    data: Record<string, unknown>;
    thingSchemaId: string;
    archivedDatetime: Date | null;
    isTargetRegistry: boolean;
};

export type UpdateRegistrySchema = {
    id?: string;
    name: string;
    displayName: string;
    isBenchling: boolean;
    isBenchlingDropdown: boolean;
    attributes: Attribute[];
    registryExperimentRelationshipType: string | null;
    isTreatmentRegistry: boolean;
    benchlingRegistryName: string | null;
    benchlingRegistryEntryIdSuffix: string | null;
    entryIdPrefix: string | null;
};

export type RegistrySchema = UpdateRegistrySchema & {
    id: string;
    archivedDatetime: Date | null;
    globalRelationOptions: RelationOptions[];
};

export type SavedRegistrySchema = RegistrySchema & SavedObject<string>;

export type RegistrySchemaWithOptions = RegistrySchema & {
    registryEntries: RegistryEntry[];
};

export async function fetchRegistryEntries(name: string): Promise<RegistryEntry[]> {
    return get(`/registries/${name}/entries`);
}

export const registriesAPI = {
    fetchAllRegistrySchemas: async (): Promise<SavedRegistrySchema[]> => {
        return get("/registries/");
    },
    fetchAllRegistrySchemasForRelationshipType: async (
        type: string
    ): Promise<SavedRegistrySchema[]> => {
        return get(`/registries?relationshipType=${type}`);
    },
    fetchRegistryEntriesForSchemaName: async (
        name: string,
        options?: { includeArchivedEntries: boolean }
    ): Promise<RegistryEntry[]> => {
        const query = options?.includeArchivedEntries ? `?includeArchived=true` : "";
        return get(`/registries/${name}/entries${query}`);
    },
    downloadRegistryEntriesTemplateForSchemaName: async (name: string) => {
        return get(`/registries/${name}/entries/template`, {
            responseType: "blob",
        });
    },
    importRegistryEntries: async (registrySchema, file): Promise<RegistryEntry[]> => {
        const formData = new FormData();
        formData.set("file", file);
        return post(`registries/${registrySchema.name}/entries/import`, formData);
    },
    unArchiveRegistryEntry: async (id) => {
        return post(`registries/entries/unarchive/${id}`);
    },
    archiveRegistryEntry: async (id) => {
        return post(`registries/entries/archive/${id}`);
    },
    deleteRegistryEntry: async (id) => {
        return callDelete(`registries/entries/${id}`);
    },
    updateRegistrySchema: async (
        registrySchema: UpdateRegistrySchema
    ): Promise<SavedRegistrySchema> => {
        return put(`registries/${registrySchema.name}`, registrySchema);
    },
    createRegistrySchema: async (registrySchema) => {
        return post(`registries`, registrySchema);
    },
};
